'use strict'

import { HomePosterInk } from './HomePosterInk.js'
import { HomePosterSoftInk } from './HomePosterSoftInk.js'

// One row in the "Your agents" home widget. State is either a real agent
// ({ _agent, id, name, caption, kind, avatar, ... } from yourAgentsRows)
// or the trailing create tile ({ _create: true }). Clicking an agent
// opens Team & agents in the home's full-screen AppWindow; clicking the
// create tile opens the Create Agent modal.
export const YourAgentCard = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'A',
  align: 'center',
  round: 'radiusControl',
  padding: 'B',
  textAlign: 'left',
  cursor: 'pointer',
  // The card sits INSIDE the agents poster (a fixed, theme-invariant hue), so
  // the shell's adaptive `title` token is wrong in one theme by construction —
  // measured rgb(4,4,4) on posterAgents #7345AF = 3.12:1 in light
  // (HOME-POSTER-NEUTRAL-INK-ON-HUE-1). `HomePosterInk` returns the poster's
  // own binary ink here and the adaptive `title` token on any neutral mount.
  color: HomePosterInk.color,
  background: 'transparent',
  border: 'none',
  width: '100%',
  transition: '160ms defaultBezier background',
  ':hover': { background: 'hover' },

  onClick: (e, el, s) => {
    e.preventDefault()
    if (s._create) {
      s.root.update({ _createAgentOpen: true, _createAgentError: null })
      return
    }
    // Talk to this agent — open the shell assistant drawer on THIS agent's own
    // thread, resumed if it has one (AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1,
    // answer C). The server resolves its persona from _targetAgentId, which
    // `openAgentThread` sets along with the thread. Falls back to the general
    // assistant's thread when the agent has no Mongo user id.
    s.root.update({ assistantOpen: true })
    el.call('openAgentThread', s.userId ? { userId: s.userId, id: s.id, name: s.name } : null)
  },

  Glyph: {
    position: 'relative',
    width: 'C',
    height: 'C',
    minWidth: 'C',
    round: 'radiusControl',
    align: 'center center',
    fontSize: 'A',
    fontWeight: '700',
    // Was fixed 'white' — BOTH branches are translucent washes that reveal
    // the adaptive page/card backdrop behind them ('overlay' is a 5% wash;
    // 'purple.3' is only 30% opacity purple over whatever sits behind), so
    // the effective composited color shifts with theme: a light lavender
    // tint in light mode (white text nearly invisible) vs. a dark muted
    // purple in dark mode (white text fine). Outside a poster `title` tracks
    // that shift; inside one the backdrop is the FIXED poster hue showing
    // through the wash, so the poster's own ink is what tracks it there.
    color: HomePosterInk.color,
    background: (el, s) => (s._create ? 'overlay' : 'purple.3'),
    text: (el, s) => (s._create ? '+' : '◆'),

    // Presence dot (board §2 "agents/youragents: agent grid (exists) +
    // presence dot per agent"). Same visual contract as OnlineMembers'
    // OnlineDot (shared/components/OnlineMembers.js) — reused here, not
    // reinvented. Deliberately STATIC 'green' rather than a live
    // heartbeat: agents carry no online/offline field anywhere in root
    // state (verified — root._agents rows are { id, userId, email, name,
    // avatar, kind } only, no presence key), and an AI agent genuinely
    // has no "offline" state in this product's data model — every agent
    // that exists IS reachable to chat. Hidden on the trailing create
    // tile (not a real agent yet).
    PresenceDot: {
      hide: (el, s) => !!s._create,
      position: 'absolute',
      // '-X' corner-offset token — same convention NotificationBell's
      // UnreadBadge uses (shared/components/NotificationBell.js), not a
      // raw px nudge.
      bottom: '-X',
      right: '-X',
      width: 'Z',
      height: 'Z',
      round: '50%',
      background: 'green',
      borderWidth: 'hairline2',
      borderStyle: 'solid',
      theme: 'dialog',
      'aria-label': (el) => el.call('polyglot', 'workspace.agents.available')
    }
  },

  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    Title: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      color: HomePosterInk.color,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) =>
        s._create
          ? el.call('polyglot', 'workspace.agents.createTitle')
          : el.call('polyglot', s.name || s.id || 'workspace.agents.fallbackName')
    },

    Caption: {
      tag: 'span',
      // Hidden on agent rows (per Nika — "remove label from
      // YourAgentsWidget") so the cards read as a clean glyph + name. Kept
      // on the trailing create tile so its CTA copy still shows.
      hide: (el, s) => !s._create,
      fontSize: 'Y',
      lineHeight: '1.4',
      // Secondary step of the SAME poster (measured `inkSoft`), not the
      // adaptive `caption` token — 2.36:1 on posterAgents before the move.
      color: HomePosterSoftInk.color,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) =>
        s._create
          ? el.call('polyglot', 'workspace.agents.createCaption')
          : el.call('polyglot', s.caption || s.kind || 'workspace.agents.fallbackName')
    }
  }

  // AssignedBadge removed 2026-06-09 per Nika — agent cards no longer
  // show the "Assigned" / "Reports to" / "Assists" pill.
}
